import type { MessageHandler, MessageOptions, MessageType } from 'element-plus';
import { ElMessage } from 'element-plus';
import type { App } from 'vue';

export type FDMessageVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

/**
 * More Options @see {@link https://element-plus.org/en-US/component/message#options}
 */
export interface FDMessageOptions extends MessageOptions {
  /** 訊息類型變數 (承接 FDMessageVariant) */
  variant?: FDMessageVariant;
}

export interface FDMessageInstance {
  (options: FDMessageOptions | string): MessageHandler;
  success: (options: FDMessageOptions | string) => MessageHandler;
  warning: (options: FDMessageOptions | string) => MessageHandler;
  info: (options: FDMessageOptions | string) => MessageHandler;
  error: (options: FDMessageOptions | string) => MessageHandler;
  primary: (options: FDMessageOptions | string) => MessageHandler;
}

const DEFAULT_CLASS = 'fd-message';

/**
 * 自定義 className
 * @param variant - 訊息類型變數 (承接 FDMessageVariant)
 * @param customClass - 自訂義 className
 */
const buildClassName = (variant?: FDMessageVariant, customClass?: string) => {
  const classes = [DEFAULT_CLASS];
  if (variant && variant !== 'default') {
    classes.push(`${DEFAULT_CLASS}--${variant}`);
  }
  if (customClass) {
    classes.push(customClass);
  }
  return classes.join(' ');
};

/**
 * 配置選項
 * @param options - 選項參數
 * @param type - 訊息類型
 * @param variant - 訊息類型變數 (承接 FDMessageVariant)
 */
const normalizeOptions = (
  options: FDMessageOptions | string,
  type?: MessageType,
  variant?: FDMessageVariant
): MessageOptions => {
  // 只有文字訊息時的選項配置
  if (typeof options === 'string') {
    return {
      plain: true,
      offset: 80,
      message: options,
      type,
      duration: 3000,
      customClass: buildClassName(variant)
    };
  }

  const resolvedVariant = options.variant ?? variant;

  return {
    ...options,
    type: type ?? options.type,
    plain: true,
    offset: 80,
    customClass: buildClassName(resolvedVariant, options.customClass)
  };
};

/**
 * FDMessage 實例
 */
const FDMessage: FDMessageInstance = ((options: FDMessageOptions | string) =>
  ElMessage(normalizeOptions(options))) as FDMessageInstance;

/**
 * 建立各訊息類型方法
 * @param type - 訊息類型
 */
const createTypedMessage = (type: MessageType) => (options: FDMessageOptions | string) =>
  ElMessage(normalizeOptions(options, type));

FDMessage.success = createTypedMessage('success');
FDMessage.warning = createTypedMessage('warning');
FDMessage.info = createTypedMessage('info');
FDMessage.error = createTypedMessage('error');
FDMessage.primary = (options: FDMessageOptions | string) =>
  ElMessage(normalizeOptions(options, 'info', 'primary'));

/**
 * 將 FDMessage 註冊為 Vue 插件
 */
export const FDMessagePlugin = {
  install(app: App) {
    app.config.globalProperties.$fdMessage = FDMessage;
  }
};

export default FDMessage;
